import extend from 'extend';
import classNames from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';
import wrap from '../ItemContainerWrapper';

import Autocomplete from '../Autocomplete';
import Checkboxes from './Checkboxes';
import CheckboxesButton from './CheckboxesButton';
import MainSelector from './MainSelector';
import TypeSelect from './TypeSelect';
import ClearAll from '../ClearAll';
import BaseSelect from '../BaseSelect';
import { checkboxOptions } from './optionsValue';
import {
  getCheckboxData,
  buildFilterItem,
  buildMainSelectorOptions,
} from './optionsHelper';
import { getChickletCategory, itemHandlerCreator } from '../../utils';
import {
  KD_COUNTRY,
  KD_STATE,
  ANY,
  USER_SPECIFIED_OPTION,
} from '../../constants';
import { typeOptions } from './optionsValue';
import optionsFactory from './options';
import { debug } from 'util';

const defaultProps = {
  underneath: true,
  subWidgetRenderedInline: true,
  locationTypeSelector: typeOptions,
  checkboxOptions,
  chicklets: {
    visible: true,
    clearAll: { visible: true },
    clearAfterSelection: true,
  },
  helpButton: {
    visible: true,
    showContent: false,
  },
  mainSelector: {
    visible: true,
    selected: 'defaultAny',
    optionValues: {
      notSelectedOption: { label: 'Anywhere' },
      specifyOption: { visible: true },
      optionGroups: [],
    },
  },
};

const propTypes = {
  theme: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
  underneath: PropTypes.bool,
  emptyItems: PropTypes.bool,
  subWidgetRenderedInline: PropTypes.bool,
  onItemAdded: PropTypes.func,
  onItemRemoved: PropTypes.func,
  kdFilterModel: PropTypes.object,
  locationTypeSelector: PropTypes.arrayOf(PropTypes.object),
  mainSelector: PropTypes.shape({
    visible: PropTypes.bool,
    notSelectedOption: PropTypes.shape({
      label: PropTypes.string,
      selected: PropTypes.bool,
    }),
    userSpecifiedOption: PropTypes.shape({
      label: PropTypes.string,
      selected: PropTypes.bool,
    }),
    optionGroups: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string,
            values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])),
            selected: PropTypes.bool,
          }),
        ),
      }),
    ),
  }),

  autoComplete: PropTypes.shape({
    url: PropTypes.string,
    key: PropTypes.string,
    bearerToken: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    minLength: PropTypes.number,
    delay: PropTypes.number,
    placeholderText: PropTypes.string,
    filters: PropTypes.arrayOf(PropTypes.object),
    relatedEntities: PropTypes.arrayOf(PropTypes.string),
    clearAfterSelection: PropTypes.bool,
    events: PropTypes.shape({
      onSuccess: PropTypes.func,
      onRender: PropTypes.func,
      onSelect: PropTypes.func,
      onFail: PropTypes.func,
      onDone: PropTypes.func,
      valueModifier: PropTypes.func,
      beforeSend: PropTypes.func,
      onFocus: PropTypes.func,
      onBlur: PropTypes.func,
      onLoadingStart: PropTypes.func,
      onLoadingEnd: PropTypes.func,
    }),

    infoBar: PropTypes.shape({
      visible: PropTypes.bool,
      leftLabel: PropTypes.string,
      rightLabel: PropTypes.string,
    }),

    // Note: deprecated?
    search: PropTypes.shape({
      url: PropTypes.func,
      data: PropTypes.array,
    }),
  }),
};

class KdSelectGeography extends React.Component {
  constructor(props) {
    super(props);

    this.mainSelector = extend(
      true,
      {},
      defaultProps.mainSelector,
      this.props.mainSelector,
    );
    this.options = this.propBasedOptions();
    this.kdFilterOptions = {
      operator: 'Eq',
      model: props.kdFilterModel,
      itemHandler: itemHandlerCreator(props.kdFilterModel.getDataType()),
    };
    this.state = {
      mainSelectorOptions: buildMainSelectorOptions(this.mainSelector),
      selecteds: [],
      selectedOption: this.options[0] || { id: null },
      selectedValue: '',
      type: KD_STATE,
    };
  }

  getDefaultSelected = () => {
    const selected = this.options.filter(o => o.isSelected)[0];
    return selected;
  };

  clearSelecteds = () => {
    this.setState({ selecteds: [] });
  };

  selectDefaultOption = () => {
    if (this.options[0]) {
      this.setState({ selectedOption: this.options[0] }, () =>
        this.props.onOptionSelected(this.state.selectedOption.id),
      );
    }
  };

  componentDidMount() {
    const { selectedValuesInitial } = this.props;
    if (selectedValuesInitial.length) {
      this.setSelectedValues(this.props.selectedValuesInitial);
    } else {
      const selectedOption = this.getDefaultSelected();
      if (selectedOption) {
        this.onSelectChange({ target: { value: selectedOption.id } }, true);
      }
    }
  }

  componentWillReceiveProps({ emptyItems }) {
    if (emptyItems && this.props.emptyItems !== emptyItems) {
      this.setState({
        selecteds: [],
      });
    }
  }

  componentDidMount() {
    const { selectedValuesInitial } = this.props;
    if (selectedValuesInitial.length) {
      this.setSelectedValues(this.props.selectedValuesInitial);
    } else {
      const selectedOption = this.getDefaultSelected();
      if (selectedOption) {
        this.onSelectChange({ target: { value: selectedOption.id } }, true);
      }
    }
  }

  buildItemToAdd = ({ value, label, category, type, entityType }) => {
    const newCategory = getChickletCategory(
      entityType || this.props.kdFilterModel.getDataType(),
    );
    const onRemove = items => {
      let selecteds = this.state.selecteds;
      const newSelecteds = selecteds.filter(({ value, type }) => {
        const data = getCheckboxData(type, value);
        const typeHasElements = data.some(v =>
          items.find(r => r.value === v.value),
        );
        return typeHasElements;
      });
      this.setState({ selecteds: newSelecteds });
    };
    return {
      key: value,
      option: value,
      value: value,
      values: [value],
      remove: onRemove,
      chickletLabel: label,
      entityType: entityType || this.state.type,
      category: newCategory,
      kdFilterOptions: this.kdFilterOptions,
      kdFilterModel: this.props.kdFilterModel,
    };
  };

  addItem = ({ values, inputVisible = null, silent = false }, callback) => {
    const {
      events: { onChange },
      autoComplete: { clearAfterSelection },
    } = this.props;
    values.forEach(({ value, label }) => {
      const remove = items => {
        if (!items.length) {
          this.selectDefaultOption();
        }
        if (this.props.isChickletNone && this.Autocomplete) {
          this.Autocomplete.setInputValue.bind(this.Autocomplete)();
        }
      };
      const event = () => {
        const data = {
          value,
          label,
          model: this.kdFilterOptions.model,
          ...this.kdFilterOptions.model.getFilterItemValue(),
        };
        onChange && onChange({ data });
        callback && callback();
      };
      if (!inputVisible) {
        clearAfterSelection && this.Autocomplete.setInputValue();
      }
      const category = getChickletCategory(this.state.type);
      this.props.onItemAdded(
        {
          value,
          remove,
          chickletLabel: label,
          option: value,
          category,
          entityType: this.state.type,
          inputVisible: inputVisible || this.isInputVisible(),
          kdFilterOptions: this.kdFilterOptions,
          kdFilterModel: this.kdFilterOptions.model,
        },
        !silent && event,
      );
    });
  };

  addCheckboxSelection = value => {
    const { selecteds, type } = this.state;
    this.setState({ selecteds: [...selecteds, { type, value }] }, () => {
      const {
        events: { onChange },
        onSetChicklets,
      } = this.props;
      const { itemsState } = this.props.wrapperState;

      let data = [...itemsState];

      this.state.selecteds.forEach(({ type, value }) => {
        data = [...data, ...getCheckboxData(type, value)];
      });

      if (data) {
        const newData = uniqBy(data.map(this.buildItemToAdd), 'value');
        const event = () => {
          const elems = {
            value,
            label: this.state.selecteds,
            model: this.kdFilterOptions.model,
            ...this.kdFilterOptions.model.getFilterItemValue(),
          };
          onChange && onChange({ data: elems });
        };
        onSetChicklets({
          chicklets: newData,
          model: this.kdFilterOptions.model,
          callback: event,
        });
      }
    });
  };

  removeCheckboxSelection = value => {
    const { selecteds, type } = this.state;
    this.setState(
      state => ({
        selecteds: state.selecteds.filter(option => option.value !== value),
      }),
      () => {
        const { itemsState } = this.props.wrapperState;
        const { onChange, onSetChicklets, onClearChicklets } = this.props;
        const data = getCheckboxData(type, value);
        let newState = itemsState;

        data.forEach(item => {
          newState.splice(newState.findIndex(v => v.value === item.value), 1);
        });

        if (newState.length) {
          newState = newState.map(this.buildItemToAdd);
          const event = () => {
            const elems = {
              value,
              label: this.state.selecteds,
              model: this.kdFilterOptions.model,
              ...this.kdFilterOptions.model.getFilterItemValue(),
            };
            onChange && onChange({ data: elems });
          };
          onSetChicklets({
            chicklets: newState,
            model: this.kdFilterOptions.model,
            callback: event,
          });
        } else {
          onClearChicklets(true);
        }
      },
    );
  };

  changeSelecteds = ({ value, checked }) => {
    if (checked) {
      this.addCheckboxSelection(value);
    } else {
      this.removeCheckboxSelection(value);
    }
  };

  changeType = type => {
    this.setState({ type });
  };

  renderCheckboxesButton = () => {
    if (this.props.underneath) {
      return null;
    }

    const { theme } = this.props;
    return (
      <CheckboxesButton
        theme={theme}
        data={this.props.checkboxOptions}
        onChange={this.changeSelecteds}
        type={this.state.type}
        selecteds={this.state.selecteds}
        isInputVisible={
          this.isInputVisible() &&
          this.mainSelector &&
          this.mainSelector.visible
        }
      />
    );
  };

  renderCheckboxesUnderneath = () => {
    if (!this.props.underneath) {
      return null;
    }
    const {
      mainSelector: { visible: selectorVisible },
    } = this;

    if (this.isInputVisible() || !selectorVisible) {
      const { theme } = this.props;
      return (
        <Checkboxes
          theme={theme}
          data={this.props.checkboxOptions}
          type={this.state.type}
          selecteds={this.state.selecteds}
          onChange={this.changeSelecteds}
          helpButton={this.props.helpButton}
        />
      );
    }
  };

  componentWillReceiveProps(nextProps) {
    if (!isEqual(nextProps.mainSelector, this.mainSelector)) {
      this.mainSelector = nextProps.mainSelector;
      this.options = this.propBasedOptions();
      const selectedOption = this.getDefaultSelected();
      if (selectedOption) {
        this.onSelectChange({ target: { value: selectedOption.id } }, true);
      }
    }
  }

  setSelectedValues = selectedValues => {
    selectedValues.forEach(({ value, label }) => {
      const { userSpecifiedOption } = this.mainSelector;
      let relatedOption;
      if (this.mainSelector.visible) {
        let inputVisible = false;
        relatedOption = this.options.find(option => option.key === value);
        if (!relatedOption && userSpecifiedOption) {
          relatedOption = this.options.find(
            option => option.key === USER_SPECIFIED_OPTION,
          );
          inputVisible = true;
        }

        if (relatedOption) {
          relatedOption.selected = { values: [value], label, option: value };
          this.addItem({
            values: [{ value, label }],
            inputVisible,
            silent: true,
          });
          this.setState(
            {
              selectedOption: relatedOption,
              selectedValue: relatedOption.selectedLabel,
            },
            () => this.props.onOptionSelected(this.state.selectedOption.id),
          );
        }
      } else {
        this.addItem({
          values: [{ value, label }],
          inputVisible: true,
          silent: true,
        });
      }
    });
  };

  propBasedOptions = () => {
    const { kdFilterModel } = this.props;
    const {
      notSelectedOption,
      userSpecifiedOption,
      optionGroups,
    } = this.mainSelector;
    let customOptions = [];

    if (!this.mainSelector.visible) {
      return [optionsFactory.create(USER_SPECIFIED_OPTION)];
    }

    if (optionGroups.length) {
      customOptions = optionGroups[0].options.map(
        option => option.values && optionsFactory.createCustom(option),
      );
    }

    if (notSelectedOption) {
      optionsFactory.setAnyDisplayName(
        notSelectedOption.label || `Any ${kdFilterModel.getEntityType()}`,
      );
      customOptions = [
        optionsFactory.create(ANY, notSelectedOption.selected),
        ...customOptions,
      ];
    }

    if (userSpecifiedOption) {
      optionsFactory.setUserSpecifiedDisplayName(
        userSpecifiedOption.label ||
          `Search ${kdFilterModel.getEntityType()}(s)...`,
      );
      customOptions = [
        ...customOptions,
        optionsFactory.create(
          USER_SPECIFIED_OPTION,
          userSpecifiedOption.selected,
        ),
      ];
    }

    return customOptions;
  };

  isInputVisible = () => this.state.selectedOption.id === USER_SPECIFIED_OPTION;

  onSelectChange = ({ target }, silent) => {
    const {
      events: { onSelect },
      onClearChicklets,
    } = this.props;
    const selectedOption = this.options.find(
      option => option.id === target.value,
    );

    if (!selectedOption) {
      return;
    }

    this.setState(
      {
        selectedOption,
        selectedValue: selectedOption.selectedLabel,
      },
      () => {
        this.props.onOptionSelected(this.state.selectedOption.id);
        onClearChicklets();
        this.setState({ selecteds: [] });
        if (selectedOption.loadDefaultValue()) {
          const callback = () => {
            const data = {
              values: selectedOption.selected.values,
              label: selectedOption.selectedLabel,
              model: this.kdFilterOptions.model,
              ...this.kdFilterOptions.model.getFilterItemValue(),
            };
            onSelect && onSelect({ data });
          };
          this.addItem(
            { ...selectedOption.selected, silent },
            !silent && callback,
          );
          this.Autocomplete && this.Autocomplete.setInputValue();
        }
      },
    );
  };

  renderAutocomplete = () => {
    const { subWidgetRenderedInline } = this.props;
    // if (subWidgetRenderedInline) {
    //   return null;
    // }

    const { api, onItemAdded, mainSelector, theme } = this.props;
    return (
      <div
        className={classNames(
          { 'u-hidden': !this.isInputVisible() },
          { [theme['u-hidden']]: !this.isInputVisible() },
        )}
      >
        <div
          className={classNames(
            'c-kd-select-geography-container__widgets',
            theme['c-kd-select-geography-container__widgets'],
          )}
        >
          {this.renderCheckboxesButton()}
          <TypeSelect
            theme={theme}
            selected={this.state.type}
            onChange={this.changeType}
            typeOptions={this.props.locationTypeSelector}
            isInputVisible={
              this.isInputVisible() &&
              this.mainSelector &&
              this.mainSelector.visible
            }
          />
        </div>
        <div
          className={classNames(
            'c-kd-select-geography-container__autocomplete',
            theme['c-kd-select-geography-container__autocomplete'],
          )}
        >
          <Autocomplete
            ref={ref => (this.Autocomplete = ref)}
            autoComplete={this.props.autoComplete}
            theme={theme}
            setSelectedItems={item => {
              this.addItem({ ...item });
            }}
            mainSelector={mainSelector}
            kdFilterModel={buildFilterItem(this.state.type)}
            hasTinySelector
            // hasInitialValues -> poner los valores iniciales si los tiene
            // lastInicialValue -> último valor inicial si lo tiene
            // isChickletNone -> si va a agregar o solo va a dejar el display
          />
        </div>
      </div>
    );
  };

  render() {
    const {
      theme,
      emptyItems,
      isChickletNone,
      chicklets,
      mainSelector,
    } = this.props;
    return (
      <div
        className={classNames(
          'c-kd-select-geography-container',
          theme['c-kd-select-geography-container'],
        )}
      >
        {mainSelector.visible && (
          <BaseSelect
            theme={theme}
            options={this.options}
            onChange={this.onSelectChange}
            defaultValue={this.state.selectedOption.id}
          />
        )}
        {this.renderAutocomplete()}
        {this.renderCheckboxesUnderneath()}
        {!emptyItems &&
          !isChickletNone &&
          chicklets.clearAll.visible && (
            <ClearAll
              theme={theme}
              onClick={() => {
                this.clearSelecteds();
                this.props.onClearChicklets();
              }}
            />
          )}
      </div>
    );
  }
}

KdSelectGeography.propTypes = propTypes;
KdSelectGeography.defaultProps = defaultProps;

export default wrap(KdSelectGeography);



// WEBPACK FOOTER //
// ./app/components/KdSelectGeography/index.js